import React from "react";
import { Tooltip } from "antd";
import { useDocumentDisplayCurrency } from "@/ProposalBuilder/hooks/useDocumentDisplayCurrency";
import { currencyFormatter } from "@/Utils/Currency";

interface PriceConversionTooltipProps {
	/**
	 * Amount in the document's QUOTE currency. When provided, the tooltip adds a
	 * "{converted} ({companyCurrency})" line showing the system-currency equivalent.
	 * Omit for non-doc-body callsites (e.g. catalog drawer rows) where the
	 * system-currency value is already what the cell shows.
	 */
	quoteAmount?: number;
	/** Hide entirely in customer-facing render contexts. */
	customerFacing?: boolean;
	/**
	 * Force the tooltip open (overrides AntD's default hover trigger). Used during
	 * inline-edit so the live conversion stays visible while the user types.
	 */
	open?: boolean;
	/** The cell / value the tooltip wraps. */
	children: React.ReactElement;
}

/**
 * Wraps a price cell. On hover, surfaces:
 *   1. (only when `quoteAmount` is supplied) the system-currency equivalent
 *      of that quote-currency amount.
 *   2. The exchange-rate context line — "1 {company} = {rate} {quote}".
 *
 * Renders the bare child for legacy documents (no doc currency configured)
 * and in customer-facing contexts.
 */
const PriceConversionTooltip: React.FC<PriceConversionTooltipProps> = ({
	quoteAmount,
	customerFacing = false,
	open,
	children
}) => {
	const { isLegacy, quoteCurrency, companyCurrency, effectiveRate } =
		useDocumentDisplayCurrency();

	if (customerFacing || isLegacy) {
		return children;
	}

	const rateLine = `1 ${companyCurrency} = ${effectiveRate} ${quoteCurrency}`;
	const systemAmount =
		quoteAmount != null && effectiveRate > 0
			? quoteAmount / effectiveRate
			: null;

	return (
		<Tooltip
			visible={open}
			title={
				<div>
					{systemAmount != null && (
						<div>
							{currencyFormatter(systemAmount, "", undefined, {
								currency: companyCurrency
							})}{" "}
							({companyCurrency})
						</div>
					)}
					<div>{rateLine}</div>
				</div>
			}
		>
			{children}
		</Tooltip>
	);
};

export default PriceConversionTooltip;
